import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";

import { useDirectoryResolveQuery } from "@/api/access";
import { QueryErrorState } from "@/api/QueryErrorState";
import { queryKeys } from "@/api/hooks";
import { QueryPendingState } from "@/api/QueryPendingState";
import {
  classifyPackFileWriteFailure,
  freezeFindings,
  usePackFileQuery,
  usePackFilesQuery,
  useRevertPackFileMutation,
  useSavePackFileMutation,
  useUndoPackFileMutation,
  type PackFileWriteFailure,
} from "@/api/packFiles";
import {
  classifyFreezeFailure,
  useFreezeCandidateMutation,
} from "@/api/preview";
import { useCapability, useSession } from "@/auth/SessionContext";
import { cn } from "@/lib/cn";
import {
  diffOptions,
  editedByNote,
  filterFiles,
  inScope,
  layerCounts,
  layerNote,
  packStackLabel,
  type DiffBase,
  type FileScope,
} from "@/lib/packFiles";
import { Button } from "@/ui/Button";
import { ConfigViewSwitch } from "@/ui/files/ConfigViewSwitch";
import { Card, CardBody } from "@/ui/Card";
import { Notice } from "@/ui/Notice";
import { DeniedState, EmptyState } from "@/ui/states";
import { DiffView } from "@/ui/files/DiffView";
import { FileEditor } from "@/ui/files/FileEditor";
import { CompanionWarnings } from "@/ui/files/CompanionWarnings";
import { FileList } from "@/ui/files/FileList";

import { FREEZE_FAILURE_COPY } from "./ProjectPreviewDialog";
import { useProjectId } from "./ProjectLayoutRoute";

/** Legacy `/projects/:id/files`: the editor now lives under Clinical configuration. */
export function ProjectFilesRedirectRoute() {
  const projectId = useProjectId();
  return (
    <Navigate
      to={`/projects/${encodeURIComponent(projectId)}/config/files`}
      replace
    />
  );
}

const STALE_COPY =
  "This file was changed by someone else. Reload to see their version.";

/**
 * "Pack files": one simple page to edit the prompt and config files of a
 * Project as plain text, then test them in Reliv Labs. The section UI is untouched.
 */
export function ProjectFilesRoute() {
  const projectId = useProjectId();
  const navigate = useNavigate();
  const read = useCapability("project.read");
  const write = useCapability("draft.write");
  const preview = useCapability("preview.create");

  const list = usePackFilesQuery(projectId);
  const { session } = useSession();
  const editors = useDirectoryResolveQuery(
    (list.data?.items ?? []).flatMap((item) => (item.editedBy ? [item.editedBy] : [])),
  );
  const noteOf = (file: { editedBy?: string | null; editedAt?: string | null }) =>
    editedByNote(file.editedBy, file.editedAt, editors.data, session.user.subject);
  const [query, setQuery] = useState("");
  // `?path=` opens a file straight away: the update result links to old text.
  const [params] = useSearchParams();
  const [selected, setSelected] = useState<string | null>(() =>
    params.get("path"),
  );
  const file = usePackFileQuery(projectId, selected);
  const save = useSavePackFileMutation(projectId);
  const revert = useRevertPackFileMutation(projectId);
  const undo = useUndoPackFileMutation(projectId);
  const freeze = useFreezeCandidateMutation(projectId);

  // The text being typed, tagged with the file revision it started from so a
  // newly loaded file or a save never shows another file's text.
  const [edit, setEdit] = useState<{ key: string; text: string } | null>(null);
  const [showDiff, setShowDiff] = useState(false);
  const [diffBase, setDiffBase] = useState<DiffBase | null>(null);
  const [scope, setScope] = useState<FileScope>("all");
  const [failure, setFailure] = useState<PackFileWriteFailure | null>(null);
  const [lostText, setLostText] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [freezeError, setFreezeError] = useState<unknown>(null);

  const data = file.data;
  const key = data ? `${data.path}#${data.revision}` : "";
  // A file the new default removed has no content: show the clinician's last text, read only.
  const text = data
    ? data.removed
      ? (data.previousContent ?? "")
      : edit?.key === key
        ? edit.text
        : data.content
    : "";
  const dirty = data !== undefined && !data.removed && text !== data.content;

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const items = list.data?.items;
  const files = useMemo(
    () =>
      filterFiles(items ?? [], query).filter((file) => inScope(file, scope)),
    [items, query, scope],
  );
  const counts = useMemo(() => layerCounts(items ?? []), [items]);

  if (!read.allowed) {
    return <DeniedState description={read.reason ?? undefined} />;
  }
  if (list.isPending) {
    return (
      <QueryPendingState
        isPaused={list.isPaused}
        failureReason={list.failureReason}
        queryKey={queryKeys.packFiles(projectId)}
        label="Loading pack files"
        errorTitle="Pack files could not be loaded"
      />
    );
  }
  if (list.isError) {
    return (
      <QueryErrorState
        error={list.error}
        onRetry={() => void list.refetch()}
        title="Pack files could not be loaded"
        subject="Pack files"
      />
    );
  }

  const choose = (path: string) => {
    if (dirty && !window.confirm("Discard your unsaved changes?")) return;
    setSelected(path);
    setEdit(null);
    setFailure(null);
    setLostText(null);
    setSaved(false);
    setShowDiff(false);
    setDiffBase(null);
  };

  const onWriteError = (error: unknown) => {
    const next = classifyPackFileWriteFailure(error);
    if (next.kind === "stale") setLostText(text);
    setFailure(next);
  };

  const doSave = () => {
    if (!data || !dirty || !write.allowed || save.isPending) return;
    setSaved(false);
    setFailure(null);
    save.mutate(
      { path: data.path, content: text, expectedRevision: data.revision },
      {
        onSuccess() {
          setEdit(null);
          setSaved(true);
        },
        onError: onWriteError,
      },
    );
  };

  const doRevert = () => {
    if (!data?.edited || !write.allowed) return;
    if (
      !window.confirm("Revert this file to the original? Your edits are lost.")
    ) {
      return;
    }
    setFailure(null);
    setSaved(false);
    revert.mutate(
      { path: data.path, expectedRevision: data.revision },
      {
        onSuccess() {
          setEdit(null);
        },
        onError: onWriteError,
      },
    );
  };

  const doUndo = () => {
    if (
      data?.removed ||
      data?.previousContent == null ||
      dirty ||
      !write.allowed
    ) {
      return;
    }
    if (!window.confirm("Undo the last save of this file? That save is lost.")) {
      return;
    }
    setFailure(null);
    setSaved(false);
    undo.mutate(
      { path: data.path, expectedRevision: data.revision },
      {
        onSuccess() {
          setEdit(null);
        },
        onError: onWriteError,
      },
    );
  };

  const doReload = () => {
    setEdit(null);
    setFailure(null);
    void file.refetch();
    void list.refetch();
  };

  const testInLab = () => {
    setFreezeError(null);
    freeze.mutate(
      { idempotencyKey: crypto.randomUUID() },
      {
        onSuccess(created) {
          void navigate(`/projects/${projectId}/lab`, {
            state: { candidateId: created.candidateId },
          });
        },
        onError: setFreezeError,
      },
    );
  };

  const testDisabledReason = !preview.allowed
    ? preview.reason
    : dirty
      ? "Save or discard your changes first"
      : null;
  const freezeFailure =
    freezeError === null ? null : classifyFreezeFailure(freezeError);
  const findings = freezeFindings(freezeError);
  const tenantName = list.data.tenantPack?.name ?? null;
  const tenantLabel = tenantName ?? "tenant";
  // Compare against the tenant pack when the file has one, else against default.
  const options = data ? diffOptions(data, tenantLabel, dirty) : [];
  const activeDiff =
    options.find((option) => option.id === diffBase) ??
    options.find((option) => option.id === "tenant") ??
    options[0];

  return (
    <div className="space-y-4">
      <ConfigViewSwitch projectId={projectId} active="files" />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">Pack files</h2>
          <p className="text-sm text-muted-foreground">
            Edit prompts and settings as plain text, then test them in Reliv Labs.
          </p>
          <p className="mt-1 text-sm font-medium" data-testid="pack-stack">
            {packStackLabel(list.data.basePack, list.data.tenantPack)}
          </p>
          <p className="max-w-prose text-xs text-muted-foreground">
            {tenantName
              ? `Files ${tenantName} does not list come from default. A ${tenantName} .txt, .md or .json file replaces the default file whole; a .yaml file is merged key by key onto it. Your edits go on top.`
              : "Files come from default. Your edits go on top."}
            {tenantName &&
            counts.overridden + counts.added + counts.identical > 0
              ? ` ${tenantName}: ${counts.overridden} overridden, ${counts.added} added${counts.identical > 0 ? `, ${counts.identical} identical to default` : ""}; ${counts.edited} edited by you.`
              : ""}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button
            type="button"
            disabled={freeze.isPending || testDisabledReason !== null}
            title={testDisabledReason ?? undefined}
            onClick={testInLab}
          >
            Test in Reliv Labs
          </Button>
          <p aria-live="polite" className="text-xs text-muted-foreground">
            {freeze.isPending
              ? "Checking and packaging…"
              : (testDisabledReason ?? "")}
          </p>
        </div>
      </div>

      {freezeFailure ? (
        <Notice tone="destructive" role="alert">
          <p>{FREEZE_FAILURE_COPY[freezeFailure.kind]}</p>
          {findings.length > 0 ? (
            <ul className="mt-1 list-disc pl-5">
              {findings.map((finding, index) => (
                <li key={index}>
                  {finding.path ? `${finding.path}: ` : ""}
                  {finding.message}
                </li>
              ))}
            </ul>
          ) : null}
        </Notice>
      ) : null}

      <CompanionWarnings
        projectId={projectId}
        fixable={write.allowed}
        fixBlockedReason={dirty ? "Save or discard your changes first" : null}
      />

      {!list.data.baseAvailable ? (
        <Notice>
          The base pack is not available here, so only tenant and edited files
          are listed.
        </Notice>
      ) : null}

      {list.data.items.length === 0 ? (
        <EmptyState
          title="No pack files"
          description="This project has no files to edit."
        />
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-[17.5rem_minmax(0,1fr)]">
          <Card>
            <CardBody>
              <FileList
                files={files}
                query={query}
                onQuery={setQuery}
                selected={selected}
                onSelect={choose}
                tenantName={tenantName}
                scope={scope}
                onScope={setScope}
                noteOf={noteOf}
                scopeCounts={{
                  all: list.data.items.length,
                  tenant: counts.overridden + counts.added,
                  mine: counts.edited,
                }}
              />
            </CardBody>
          </Card>

          <Card>
            <CardBody className="space-y-3">
              {selected === null ? (
                <p className="text-sm text-muted-foreground">
                  Pick a file on the left to edit it.
                </p>
              ) : file.isPending ? (
                <QueryPendingState
                  isPaused={file.isPaused}
                  failureReason={file.failureReason}
                  queryKey={queryKeys.packFile(projectId, selected)}
                  label="Loading file"
                  errorTitle="This file could not be loaded"
                />
              ) : file.isError ? (
                <QueryErrorState
                  error={file.error}
                  onRetry={() => void file.refetch()}
                  title="This file could not be loaded"
                  subject="Pack files"
                />
              ) : data ? (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p
                      dir="ltr"
                      className="break-all font-mono text-sm font-medium"
                    >
                      {data.path}
                    </p>
                    <p role="status" className="text-sm text-muted-foreground">
                      {data.removed
                        ? "Removed in the new default"
                        : saved && !dirty
                          ? "Saved"
                          : dirty
                            ? "Unsaved changes"
                            : data.edited
                              ? "Edited"
                              : "Original"}
                    </p>
                  </div>

                  {noteOf(data) ? (
                    <p className="text-xs text-muted-foreground">
                      {noteOf(data)}
                    </p>
                  ) : null}

                  {tenantName ? (
                    <p className="text-xs text-muted-foreground">
                      {layerNote(data.layer, data.path, tenantName)}
                    </p>
                  ) : null}

                  <FileEditor
                    label={`Content of ${data.path}`}
                    value={text}
                    readOnly={!write.allowed || data.removed}
                    onChange={(next) => {
                      setSaved(false);
                      setEdit({ key, text: next });
                    }}
                    onSave={doSave}
                  />

                  {!write.allowed ? <Notice>{write.reason}</Notice> : null}
                  {data.removed ? (
                    <Notice>
                      The new default no longer has this file. Copy the text you
                      need.
                    </Notice>
                  ) : null}

                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      disabled={!dirty || !write.allowed || save.isPending}
                      onClick={doSave}
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={
                        data.removed ||
                        data.previousContent == null ||
                        dirty ||
                        !write.allowed ||
                        undo.isPending
                      }
                      title={
                        dirty
                          ? "Save or discard your changes first"
                          : data.previousContent == null
                            ? "No earlier save of this file to undo"
                            : undefined
                      }
                      onClick={doUndo}
                    >
                      Undo last save
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={
                        data.removed ||
                        !data.edited ||
                        !write.allowed ||
                        revert.isPending
                      }
                      onClick={doRevert}
                    >
                      Revert to original
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      aria-expanded={showDiff}
                      disabled={data.removed}
                      onClick={() => setShowDiff((value) => !value)}
                    >
                      Show changes
                    </Button>
                  </div>

                  {failure?.kind === "invalid" || failure?.kind === "error" ? (
                    <Notice tone="destructive" role="alert">
                      {failure.message}
                    </Notice>
                  ) : null}
                  {failure?.kind === "denied" ? (
                    <Notice tone="destructive" role="alert">
                      You are not allowed to change this file.
                    </Notice>
                  ) : null}
                  {failure?.kind === "stale" ? (
                    <Notice
                      tone="destructive"
                      role="alert"
                      action={
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={doReload}
                        >
                          Reload
                        </Button>
                      }
                    >
                      {STALE_COPY}
                    </Notice>
                  ) : null}
                  {lostText !== null ? (
                    <details className="text-sm">
                      <summary className="cursor-pointer">Your version</summary>
                      <pre
                        dir="auto"
                        className="mt-1 max-h-60 overflow-auto whitespace-pre-wrap rounded-md border border-border p-2 font-mono text-xs"
                      >
                        {lostText}
                      </pre>
                    </details>
                  ) : null}

                  {showDiff && activeDiff ? (
                    <>
                      <div
                        role="group"
                        aria-label="Compare with"
                        className="flex flex-wrap gap-1 text-xs"
                      >
                        {options.map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            aria-pressed={option.id === activeDiff.id}
                            onClick={() => setDiffBase(option.id)}
                            className={cn(
                              "rounded-md border border-border px-2 py-1 font-medium text-muted-foreground hover:text-foreground",
                              option.id === activeDiff.id &&
                                "bg-primary text-primary-foreground hover:text-primary-foreground",
                            )}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                      <DiffView original={activeDiff.text} current={text} />
                    </>
                  ) : null}

                  <p className="text-xs text-muted-foreground">
                    YAML edits are merged onto the base file, so a key you
                    remove here is still inherited from the base.
                  </p>
                </>
              ) : null}
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
}
